import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { classNames, transformRoute } from "helpers";
import { downloadCsv, toCsv } from "helpers/csv";
import Button from "ui/components/Button/Button/Button";
import { getFinanceVehiclesAPI } from "services/api/reports/finance/api";
import { VehicleFinanceRow } from "services/api/reports/finance/types";
import { useDashboardDateRange } from "ui/modules/dashboard/pages/DashboardPage/context/DashboardDateRangeContext";
import { TableSearch } from "ui/components/Table/TableSearch";
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";
import { initialPagination } from "ui/components/Table/consts";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { VEHICLE_FILTER_OPTIONS, VEHICLE_FINANCE_COLUMNS } from "./consts";
import { VehicleFilter } from "./types";

const VehicleProfitabilityTab: React.FC = () => {
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();
  const [rows, setRows] = useState<VehicleFinanceRow[]>([]);
  const [filter, setFilter] = useState<VehicleFilter>("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getFinanceVehiclesAPI(range.from, range.to)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setIsLoading(false));
  }, [range.from, range.to]);

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: vehicleId }));
    },
    [navigate]
  );

  const visibleRows = useMemo(() => {
    if (filter === "sold") return rows.filter((row) => row.isSold);
    if (filter === "inStock") return rows.filter((row) => !row.isSold);

    return rows;
  }, [rows, filter]);

  const columns = useMemo(
    () => VEHICLE_FINANCE_COLUMNS(handleVehicleClick),
    [handleVehicleClick]
  );

  const handleExport = useCallback(() => {
    const rows = visibleRows.map((row) => ({
      Vozilo: row.vehicleName,
      Registracija: row.registrationPlate,
      Nabava: row.purchase,
      Ulaganja: row.investment,
      Prodaja: row.isSold ? row.sale : null,
      Zarada: row.profit,
      "Marža %": row.marginPercent,
      "Dana na stanju": row.daysInStock,
    }));

    downloadCsv(
      toCsv(rows, [
        "Vozilo",
        "Registracija",
        "Nabava",
        "Ulaganja",
        "Prodaja",
        "Zarada",
        "Marža %",
        "Dana na stanju",
      ]),
      "profitabilnost-vozila.csv"
    );
  }, [visibleRows]);

  return (
    <div className="px-0 py-6 xl:px-6 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 rounded-md bg-slate-100 p-1">
          {VEHICLE_FILTER_OPTIONS.map((option) => {
            const handleFilterClick = () => setFilter(option.value);

            return (
              <button
                key={option.value}
                type="button"
                onClick={handleFilterClick}
                className={classNames(
                  "px-4 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none",
                  filter === option.value
                    ? "bg-white text-sky-600 shadow-sm"
                    : "text-gray-500 hover:text-sky-600"
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <Button variant="outlined" size="small" onClick={handleExport}>
          Izvezi
        </Button>
      </div>

      {isLoading ? (
        <div className="relative min-h-[200px]">
          <Loader isLoading={true}> </Loader>
        </div>
      ) : visibleRows.length === 0 ? (
        <NoResults message="Nema podataka za odabrani period" />
      ) : (
        <>
          <TableSearch
            columns={columns}
            data={visibleRows}
            selectableRows={false}
            disablePagination={true}
            pagination={{
              ...initialPagination,
              totalCount: visibleRows.length,
              totalPages: 1,
            }}
          />
          <div className="text-xs text-slate-400">
            Zbroj ovog taba ne odgovara Sažetku iz dva razloga: opći troškovi bez vozila
            nisu raspodijeljeni, a redak pokriva cijeli životni vijek vozila dok Sažetak
            zbraja samo iznose iz odabranog razdoblja.
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleProfitabilityTab;
